import { Check, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCopy } from '../../hooks/useCopy.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../shadcn/index.js';

import { Button } from './Button.js';

export interface CopyButtonProps {
  /** The exact text written to the clipboard. */
  value: string;
  /** Optional visible text beside the icon. */
  label?: string;
  /** Also fire a success toast (use for prominent secrets, e.g. the full API key). */
  toast?: boolean;
  size?: 'sm' | 'md';
  variant?: 'ghost' | 'secondary';
  className?: string;
  /** Subject appended to the aria-label / tooltip, e.g. "API key". */
  subject?: string;
}

/**
 * Small click-to-copy button: writes `value` to the clipboard, swaps Copy→Check for
 * ~1.8s, shows a "Copy"/"Copied" tooltip, and (optionally) a toast. Drop-in anywhere —
 * composes the ui Button + shadcn Tooltip + the useCopy hook.
 */
export function CopyButton({
  value,
  label,
  toast: withToast = false,
  size = 'sm',
  variant = 'ghost',
  className,
  subject,
}: CopyButtonProps): JSX.Element {
  const { t } = useTranslation();
  const { copy, copied } = useCopy();
  const isCopied = copied === value;

  const onClick = async (): Promise<void> => {
    const ok = await copy(value);
    if (ok && withToast) toast.success(t('actions.copied', { defaultValue: 'Copied' }));
  };

  const tip = isCopied ? t('actions.copied', { defaultValue: 'Copied' }) : t('actions.copy', { defaultValue: 'Copy' });
  const aria = subject ? `${tip} — ${subject}` : tip;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button type="button" variant={variant} size={size} onClick={() => void onClick()} aria-label={aria} className={className}>
            {isCopied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
            {label && <span className="ms-1.5">{label}</span>}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
